import { useEffect, useMemo, useState } from 'react';
import { Button, Grid, Popover } from '@mui/material';
import { Form } from 'react-final-form';
import { useDispatch, useSelector } from 'react-redux';
import { difference, isEmpty } from 'lodash';

import TextField from 'common/forms/TextField';
import MultiPracticeSelectField from 'common/forms/MultiPracticeSelectField';
import { casingFormat } from 'common/utils';
import { getPartnerPractices } from 'store/thunks/partnerThunks';
import { updateShipstationFilter } from '../../../store/thunks/shipstationShipmentThunks';

const ShipstationFilterButton = () => {
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = useState(null);
  const [practices, setPractices] = useState([]);
  const [selectedPartnerIds, setSelectedPartnerIds] = useState([]);
  const [formInitialValues, setFormInitialValues] = useState({});
  const [isLoadingPractices, setIsLoadingPractices] = useState(false);

  const filter = useSelector(({ shipstationShipment }) => shipstationShipment.list.filter);
  const partners = useSelector(({ auth, partner }) => partner[auth.currentTenantId]);

  const popoverId = anchorEl ? 'ShipstationFilter-Popover' : undefined;

  const partnerOptions = useMemo(
    () =>
      partners?.map(({ partnerName, partnerUuid }) => ({
        value: partnerUuid,
        label: casingFormat(partnerName),
      })) ?? [],
    [partners]
  );

  const practiceOptions = useMemo(
    () =>
      practices?.map(({ practiceId, practiceName }) => ({
        value: practiceId,
        label: casingFormat(practiceName),
      })) ?? [],
    [practices]
  );

  useEffect(() => {
    if (isEmpty(selectedPartnerIds)) {
      setPractices([]);
      setIsLoadingPractices(false);
      return;
    }

    setIsLoadingPractices(true);
    dispatch(getPartnerPractices({ partnerIds: selectedPartnerIds }))
      .then((practice) => {
        if (practice) {
          setPractices(practice);
        }
      })
      .finally(() => {
        setIsLoadingPractices(false);
      });
  }, [selectedPartnerIds, dispatch]);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);

    const selectedPartners = partnerOptions.filter((partner) =>
      filter?.partnerIds?.includes(partner.value)
    );
    const partnerIds = selectedPartners.map((partner) => partner.value);
    if (!isEmpty(partnerIds)) {
      setSelectedPartnerIds(partnerIds);
    }

    const selectedPractices = practiceOptions.filter((practice) =>
      filter?.practiceIds?.includes(practice.value)
    );

    setFormInitialValues({
      orderId: filter?.orderId,
      trackingNumber: filter?.trackingNumber,
      partnerIds: selectedPartners,
      practiceIds: selectedPractices,
    });
  };

  const handleClose = () => setAnchorEl(null);

  const handlePartnerChange = (newValue, form) => {
    const partnerIds = newValue.map((partner) => partner.value);
    const removedPartnerIds = difference(selectedPartnerIds, partnerIds);

    setSelectedPartnerIds(partnerIds);

    if (!isEmpty(removedPartnerIds)) {
      const currentPractices = form.getState().values.practiceIds || [];
      const updatedPractices = currentPractices.filter(({ value: practiceId }) => {
        const practice = practices.find((p) => p.practiceId === practiceId);
        return practice && !removedPartnerIds.includes(practice.partnerId);
      });
      form.change('practiceIds', updatedPractices);
    }

    form.change('partnerIds', newValue);

    if (isEmpty(partnerIds)) {
      setPractices([]);
      form.change('practiceIds', []);
    }
  };

  const handleApplyFilter = (formData) => {
    setAnchorEl(null);

    dispatch(
      updateShipstationFilter({
        filter: {
          orderId: formData.orderId || undefined,
          trackingNumber: formData.trackingNumber || undefined,
          partnerIds: !isEmpty(formData.partnerIds)
            ? formData.partnerIds.map((p) => p.value)
            : undefined,
          practiceIds: !isEmpty(formData.practiceIds)
            ? formData.practiceIds.map((p) => p.value)
            : undefined,
        },
      })
    );
  };

  return (
    <>
      <Button
        sx={{ height: 33 }}
        variant='outlined'
        color='secondary'
        onClick={handleOpen}
        aria-describedby={popoverId}
      >
        Filters
      </Button>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Grid container sx={{ p: 1.5, width: 325 }} spacing={2} direction='column'>
          <Grid item>
            <Form
              initialValues={formInitialValues}
              onSubmit={handleApplyFilter}
              render={({ handleSubmit, invalid, form }) => (
                <form onSubmit={handleSubmit} noValidate>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Order ID'
                      name='orderId'
                      id='ShipstationFilter-orderId-input'
                    />
                  </Grid>
                  <Grid item sx={{ py: 0.5 }}>
                    <TextField
                      label='Tracking Number'
                      name='trackingNumber'
                      id='ShipstationFilter-trackingNumber-input'
                    />
                  </Grid>
                  <MultiPracticeSelectField
                    label='Partner'
                    name='partnerIds'
                    id='ShipstationFilter-partnerIds-dropdown'
                    options={partnerOptions}
                    onChange={(event, newValue) => {
                      handlePartnerChange(newValue, form);
                    }}
                  />
                  <MultiPracticeSelectField
                    label='Practice'
                    name='practiceIds'
                    id='ShipstationFilter-practiceIds-dropdown'
                    options={practiceOptions}
                    disabled={isEmpty(selectedPartnerIds) || isEmpty(practiceOptions)}
                    loading={isLoadingPractices}
                  />
                  <Grid container justifyContent='flex-end'>
                    <Button variant='contained' type='submit' disabled={invalid}>
                      Apply
                    </Button>
                  </Grid>
                </form>
              )}
            />
          </Grid>
        </Grid>
      </Popover>
    </>
  );
};

export default ShipstationFilterButton;
